import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Outlet,
  Link,
  useParams,
  useNavigate,
} from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuIcon from '@mui/icons-material/Menu';
import { useFieldguideStore, useAuthStore } from '../../stores/root_store';
import type { FieldguideSection } from '../../types';

export const FieldGuidePage = observer(() => {
  const { guideType = 'general', entrySlug } = useParams();
  const navigate = useNavigate();
  const fieldguideStore = useFieldguideStore();
  const authStore = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isManager = authStore.roles.includes('manager');
  const canSeeMentor = authStore.isMentor || isManager;

  // Only show mentor tab to mentors/managers
  const guideTypes = canSeeMentor
    ? [{ value: 'general', label: 'General' }, { value: 'mentor', label: 'Mentor' }]
    : [{ value: 'general', label: 'General' }];

  // Redirect non-mentors away from mentor guide
  useEffect(() => {
    if (guideType === 'mentor' && !canSeeMentor) {
      navigate('/assistant/fieldguide/general', { replace: true });
    }
  }, [guideType, canSeeMentor, navigate]);

  const [createSectionOpen, setCreateSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSection, setEditingSection] = useState<FieldguideSection | null>(null);
  const [editSectionName, setEditSectionName] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Fetch sections when guide type changes
  useEffect(() => {
    if (authStore.initialized) {
      fieldguideStore.fetchSections(guideType);
    }
  }, [authStore.initialized, guideType, fieldguideStore]);

  // Fetch entries for all sections
  useEffect(() => {
    for (const section of fieldguideStore.sections) {
      if (!fieldguideStore.entriesBySectionSlug.has(section.slug)) {
        fieldguideStore.fetchEntries(section.slug);
      }
    }
    // Auto-expand all sections
    if (fieldguideStore.sections.length > 0) {
      setExpandedSections(new Set(fieldguideStore.sections.map((s) => s.slug)));
    }
  }, [fieldguideStore.sections, fieldguideStore]);

  const toggleSection = (slug: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const handleCreateSection = async () => {
    if (!newSectionName.trim()) return;
    const section = await fieldguideStore.createSection({
      name: newSectionName.trim(),
      guide_type: guideType,
      order: fieldguideStore.sections.length,
    });
    if (section) {
      setCreateSectionOpen(false);
      setNewSectionName('');
    }
  };

  const handleUpdateSection = async () => {
    if (!editingSection || !editSectionName.trim()) return;
    await fieldguideStore.updateSection(editingSection.slug, {
      name: editSectionName.trim(),
    });
    setEditingSection(null);
    setEditSectionName('');
  };

  const handleDeleteSection = async (slug: string) => {
    if (!confirm('Delete this section and all its entries?')) return;
    await fieldguideStore.deleteSection(slug);
  };

  const handleEntryClick = (slug: string) => {
    const el = document.getElementById(`entry-${slug}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (isMobile) setMobileDrawerOpen(false);
  };

  const handleSectionClick = (slug: string) => {
    const el = document.getElementById(`section-${slug}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (isMobile) setMobileDrawerOpen(false);
  };

  const sidebarContent = (
    <Box sx={{ width: isMobile ? 280 : '100%' }}>
      {isManager && (
        <Box sx={{ p: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateSectionOpen(true)}
            fullWidth
            variant="outlined"
          >
            New Section
          </Button>
        </Box>
      )}

      {fieldguideStore.sectionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : fieldguideStore.sections.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No sections yet.
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {fieldguideStore.sections.map((section) => {
            const entries = fieldguideStore.getEntriesForSection(section.slug);
            const isExpanded = expandedSections.has(section.slug);

            return (
              <Box key={section.uuid}>
                <ListItemButton
                  onClick={() => {
                    toggleSection(section.slug);
                    handleSectionClick(section.slug);
                  }}
                  sx={{ py: 0.5 }}
                >
                  <ListItemText
                    primary={section.name}
                    primaryTypographyProps={{
                      variant: 'subtitle2',
                      fontWeight: 'bold',
                    }}
                  />
                  {isManager && (
                    <Box sx={{ display: 'flex', mr: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSection(section);
                          setEditSectionName(section.name);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSection(section.slug);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={isExpanded}>
                  <List dense disablePadding>
                    {entries.map((entry) => (
                      <ListItemButton
                        key={entry.uuid}
                        onClick={() => handleEntryClick(entry.slug)}
                        selected={entrySlug === entry.slug}
                        sx={{ pl: 4 }}
                      >
                        <ListItemText
                          primary={entry.title}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItemButton>
                    ))}
                    {isManager && (
                      <ListItemButton
                        sx={{ pl: 4 }}
                        onClick={() =>
                          navigate(
                            `/assistant/fieldguide/${guideType}/new?section=${section.slug}`
                          )
                        }
                      >
                        <AddIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText
                          primary="Add Entry"
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItemButton>
                    )}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {isMobile && (
            <IconButton onClick={() => setMobileDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          <MenuBookIcon color="primary" />
          <Typography variant="h4">Field Guide</Typography>
        </Box>

        {/* Guide Type Tabs */}
        <Tabs
          value={guideType}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {guideTypes.map((gt) => (
            <Tab
              key={gt.value}
              value={gt.value}
              label={gt.label}
              component={Link}
              to={`/assistant/fieldguide/${gt.value}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Layout: sidebar + content */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Sidebar - desktop */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              pr: 1,
            }}
          >
            {sidebarContent}
          </Box>
        )}

        {/* Sidebar - mobile drawer */}
        {isMobile && (
          <Drawer
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
          >
            {sidebarContent}
          </Drawer>
        )}

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>

      {/* Create Section Dialog */}
      <Dialog
        open={createSectionOpen}
        onClose={() => {
          setCreateSectionOpen(false);
          setNewSectionName('');
          fieldguideStore.clearErrors();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Section</DialogTitle>
        <DialogContent>
          <TextField
            label="Section Name"
            fullWidth
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            disabled={fieldguideStore.saving}
            required
            sx={{ mt: 1 }}
          />
          {fieldguideStore.saveError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {fieldguideStore.saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateSectionOpen(false);
              setNewSectionName('');
            }}
            disabled={fieldguideStore.saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSection}
            variant="contained"
            disabled={!newSectionName.trim() || fieldguideStore.saving}
          >
            {fieldguideStore.saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog
        open={!!editingSection}
        onClose={() => {
          setEditingSection(null);
          setEditSectionName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Section</DialogTitle>
        <DialogContent>
          <TextField
            label="Section Name"
            fullWidth
            value={editSectionName}
            onChange={(e) => setEditSectionName(e.target.value)}
            disabled={fieldguideStore.saving}
            required
            sx={{ mt: 1 }}
          />
          {fieldguideStore.saveError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {fieldguideStore.saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingSection(null);
              setEditSectionName('');
            }}
            disabled={fieldguideStore.saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSection}
            variant="contained"
            disabled={!editSectionName.trim() || fieldguideStore.saving}
          >
            {fieldguideStore.saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});
